import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as echarts from 'echarts';
import { GlobalFilterService } from './global-filter-service';
import { ChartFilterHandlerService } from './chart-filter-handler.service';
import {
  buildLegendSelectedState,
  applyOpacityToSeries,
  resetSeriesOpacity,
} from '../utils/chart.utils';
import {
  buildLegendSyncData,
  buildCategoryFilterData,
  findIndicesByNames,
  matchValue,
  MatchMode,
} from '../utils/filter.utils';
import {
  getColorsForIndices,
  findIndicesByColors,
  ColorConfig,
} from '../utils/color.utils';
import { extractGroupFromCategory } from '../utils/axis.utils';

/**
 * Callback interface for the component to provide chart-specific behavior.
 * The wrapper calls these when it needs the component to do something.
 */
export interface ChartWrapperCallbacks {
  /** Called when the wrapper determines a data reload is needed. Component should fetch + re-render. */
  onReload: (filters: any) => void;
  /** Called to get the current ECharts instance (may be null before init). */
  getChart: () => echarts.ECharts | null;
  /** Called to get all series/slice names for legend sync. */
  getSeriesNames: () => string[];
  /** Called to get all category names (x-axis labels) for category filtering. */
  getCategories: () => string[];

  // --- Optional custom highlight overrides ---
  // If provided, the wrapper uses these instead of its built-in opacity-based highlight.
  // This lets components with custom visual behavior (color-based, donut dim, etc.) still use the wrapper.

  /** Custom category highlight (e.g., waterfall uses 0.3 opacity, horizontal-bar uses color swap). */
  onHighlightCategories?: (categories: string[]) => void;
  /** Custom category reset. */
  onResetCategoryHighlight?: () => void;
  /** Custom legend/slice selection (e.g., donut dims non-selected slices). */
  onSelectLegendsByIndices?: (indices: number[]) => void;
}

/**
 * Chart type hint — determines which click/sync behaviors are used.
 * - 'category': Charts with x-axis categories (bar, line, stacked-bar, area, etc.)
 * - 'legend-only': Charts without x-axis categories (pie, donut, treemap, etc.)
 */
export type ChartType = 'category' | 'legend-only';

/**
 * Centralized Wrapper Service for all ECharts components.
 *
 * Handles:
 *  1. Filter listener subscription (shouldReload, legendSync, category filter)
 *  2. Chart click events (label, legend, double-axis, data point)
 *  3. Legend click events (select/deselect, multi-select)
 *  4. Category selection & highlight (single/multi-select, toggle)
 *  5. Legend sync broadcasting & receiving (index, color, name based)
 *  6. Color consistency (register/retrieve from global color map)
 *  7. Double x-axis / y-axis click handling
 *
 * Usage in a component:
 * ```
 *   wrapper = inject(ChartWrapperService).createInstance();
 *
 *   ngAfterViewInit() {
 *     this.wrapper.attach(this.data, {
 *       onReload: (f) => this.loadAndRenderChart(f),
 *       getChart: () => this.myChart,
 *       getSeriesNames: () => this.allSeriesNames,
 *       getCategories: () => this.allCategories,
 *     });
 *   }
 *
 *   // After calling setOption in your render method:
 *   this.wrapper.afterRender();
 *
 *   ngOnDestroy() {
 *     this.wrapper.detach();
 *   }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ChartWrapperService {
  constructor(
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
    private router: Router,
  ) {}

  /**
   * Create a new wrapper instance for a chart component.
   * Each component gets its own instance to track its own state.
   */
  createInstance(): ChartWrapperInstance {
    return new ChartWrapperInstance(this.globalFilterService, this.chartFilterHandler, this.router);
  }
}

/**
 * Per-component wrapper instance. Holds all state and event wiring for one chart.
 */
export class ChartWrapperInstance {
  // --- State ---
  selectedCategories: string[] = [];
  currentlySelectedIndices: number[] = [];
  private selectedYAxisValue: { axisIndex: number; value: number } | null = null;
  private selectedYAxisValues: { axisIndex: number; value: number }[] = [];
  private isInternalLegendChange = false;
  /** Stores original API data items keyed by category value, for filterKeyMap field extraction. */
  private sourceDataMap = new Map<string, any>();
  private filterSub?: Subscription;
  private data: any = null;
  private callbacks: ChartWrapperCallbacks | null = null;
  private chartType: ChartType = 'category';

  constructor(
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
    private router: Router,
  ) {}

  // ========================
  // LIFECYCLE
  // ========================

  /**
   * Attach the wrapper to a chart component.
   * Sets up the filter$ subscription and stores config + callbacks.
   *
   * @param data - The widget config (from @Input() data)
   * @param callbacks - Component callbacks for reload, getChart, etc.
   * @param chartType - 'category' for axis charts, 'legend-only' for pie/donut/treemap
   */
  attach(data: any, callbacks: ChartWrapperCallbacks, chartType: ChartType = 'category'): any | null {
    this.data = data;
    this.callbacks = callbacks;
    this.chartType = chartType;
    this.subscribeToFilters();

    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(data?.widgetId);
    if (navigationFilters) {
      return navigationFilters;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters();
    if (
      this.hasInitialFilterState(currentFilters) &&
      this.chartFilterHandler.shouldReload(currentFilters, data?.widgetId)
    ) {
      return currentFilters;
    }

    // When requiredFilterKey is set and met, this chart was dynamically created
    // after the required filter was selected. Return current filters so the chart
    // can use them in its initial load (avoids a wasted empty-payload call).
    if (data?.requiredFilterKey && this.isRequiredFilterMet()) {
      return currentFilters;
    }
    return null;
  }

  /**
   * Call after each render (after setOption) to re-apply visual state and wire events.
   */
  afterRender(): void {
    const chart = this.callbacks?.getChart();
    if (!chart) return;

    // Re-apply active visual selection after data reload
    if (this.chartType === 'category' && this.selectedCategories.length > 0) {
      this.highlightCategories(this.selectedCategories);
    } else if (this.currentlySelectedIndices.length > 0) {
      this.selectLegendsByIndices(this.currentlySelectedIndices);
    }

    this.setupChartClickEvent();
    this.setupLegendClickEvent();
  }

  /**
   * Detach: clean up subscription and event listeners.
   */
  detach(): void {
    this.filterSub?.unsubscribe();
    this.filterSub = undefined;
    this.callbacks = null;
    this.data = null;
    this.selectedCategories = [];
    this.currentlySelectedIndices = [];
    this.selectedYAxisValue = null;
    this.selectedYAxisValues = [];
    this.sourceDataMap.clear();
  }

  /**
   * Update widget config (e.g. if @Input() data changes).
   */
  updateData(data: any): void {
    this.data = data;
  }

  /**
   * Store original API data items so filterKeyMap can extract field-specific values at click time.
   * Call this after loading chart data.
   * Accepts multiple keys to index by (e.g., categoryKey + seriesKey).
   */
  setSourceData(data: any[], ...keys: string[]): void {
    this.sourceDataMap.clear();
    if (!data || !keys.length) return;
    for (const item of data) {
      for (const k of keys) {
        const val = String(item?.[k] ?? '');
        if (val && !this.sourceDataMap.has(val)) {
          this.sourceDataMap.set(val, item);
        }
      }
    }
  }

  // ========================
  // COLOR CONSISTENCY
  // ========================

  /**
   * Apply color consistency for a series item.
   * Returns the resolved color (global or provided).
   */
  applyColorConsistency(seriesName: string, currentColor: string | null | undefined): string | null | undefined {
    if (!this.data?.enableColorConsistency) return currentColor;

    const globalColor = this.chartFilterHandler.getItemColor(seriesName);
    if (globalColor) {
      return globalColor;
    } else if (currentColor) {
      this.chartFilterHandler.registerItemColor(seriesName, currentColor);
    }
    return currentColor;
  }

  // ========================
  // FILTER SUBSCRIPTION
  // ========================

  /**
   * Check whether the requiredFilterKey (if configured) has at least one value selected.
   * Returns true if no requiredFilterKey is set, or the condition is met.
   */
  isRequiredFilterMet(): boolean {
    const key = this.data?.requiredFilterKey;
    if (!key) return true;
    const filters = this.globalFilterService.getCurrentFilters();
    const value = filters?.requestParams?.[key] ?? filters?.queryParams?.[key];
    return Array.isArray(value) && value.length >= 1;
  }

  private subscribeToFilters(): void {
    this.filterSub?.unsubscribe();

    this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.data?.widgetId || !this.callbacks) return;

      // Skip reload if requiredFilterKey is set but not satisfied
      if (!this.isRequiredFilterMet()) {
        return;
      }

      if (!this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        return;
      }

      // Handle legendSync (visual dimming from other charts)
      if (filters?.legendSync) {
        this.handleExternalLegendSync(filters);
        // For category filters, also trigger data reload
        if (filters.isCategoryFilter) {
          let filtersForReload = filters;
          if (filters?.listenerExtraParams?.[this.data.widgetId]) {
            filtersForReload = {
              ...filters,
              requestParams: {
                ...filters.requestParams,
                ...filters.listenerExtraParams[this.data.widgetId],
              },
            };
          }
          this.callbacks.onReload(filtersForReload);
        }
        return;
      }

      // Clear local selection state on global dropdown change only
      if (!filters?.widgetId) {
        this.selectedCategories = [];
        this.currentlySelectedIndices = [];
        const chart = this.callbacks.getChart();
        if (chart) {
          this.selectLegendsByIndices([]); // reset legend visual state
        }
      }

      let filtersToUse = filters;
      if (filters?.listenerExtraParams?.[this.data.widgetId]) {
        filtersToUse = {
          ...filters,
          requestParams: {
            ...filters.requestParams,
            ...filters.listenerExtraParams[this.data.widgetId],
          },
        };
      }
      this.callbacks.onReload(filtersToUse);
    });
  }

  private hasInitialFilterState(filters: any): boolean {
    return !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );
  }

  // ========================
  // CHART CLICK EVENT
  // ========================

  private setupChartClickEvent(): void {
    const chart = this.callbacks?.getChart();
    if (!chart) return;

    chart.off('click');
    chart.on('click', (params: any) => {
      if (this.chartType === 'legend-only') {
        this.handleLegendOnlyChartClick(params);
        return;
      }
      this.handleCategoryChartClick(params);
    });
  }

  /** Click handler for category-based charts (bar, line, stacked-bar, etc.) */
  private handleCategoryChartClick(params: any): void {
    if (this.handleNavigationAction(params)) return;

    // Ignore legend clicks — handled by legendselectchanged
    if (params?.componentType === 'legend') {
      params?.event?.event?.preventDefault?.();
      params?.event?.event?.stopPropagation?.();
      return;
    }

    // X-axis click
    if (params.componentType === 'xAxis') {
      const axisIndex = params.xAxisIndex ?? params.axisIndex ?? 0;
      if (axisIndex === 1 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
        this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
        this.highlightByGroup(params.value);
      } else if (this.data?.enableLabelClick) {
        this.handleLabelClick(params.value);
      }
      return;
    }

    // Y-axis click
    if (params.componentType === 'yAxis') {
      const axisIndex = params.yAxisIndex ?? 0;
      if (this.data?.enableYAxisClick || this.data?.chartConfig?.enableYAxisClick) {
        this.handleYAxisClick(params);
        return;
      }
      if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
        this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
      }
      return;
    }

    // Data point / series click
    if (params.componentType === 'series') {
      if (this.data?.enableLabelClick) {
        this.handleLabelClick(params.name);
      } else if (this.data?.enableLegendClick) {
        // Series click when legendClick is enabled — treat as legend selection
        this.handleSeriesAsLegendClick(params);
      } else {
        const rawItem = this.sourceDataMap.get(String(params.name)) || params.data;
        this.chartFilterHandler.updateFilterSelection(this.data, params.name, rawItem);
      }
    }
  }

  /** Click handler for legend-only charts (pie, donut, treemap) */
  private handleLegendOnlyChartClick(params: any): void {
    if (this.handleNavigationAction(params)) return;

    if (this.data?.enableLabelClick || this.data?.enableLegendClick) {
      this.handleSliceClick(params.name);
    } else {
      const rawItem = this.sourceDataMap.get(String(params.name)) || params.data;
      this.chartFilterHandler.updateFilterSelection(this.data, params.name, rawItem);
    }
  }

  // ========================
  // LEGEND CLICK EVENT
  // ========================

  private setupLegendClickEvent(): void {
    const chart = this.callbacks?.getChart();
    if (!chart) return;

    chart.off('legendselectchanged');
    chart.on('legendselectchanged', (params: any) => {
      if (this.isInternalLegendChange) return;

      params?.event?.event?.preventDefault?.();
      params?.event?.event?.stopPropagation?.();

      if (this.data?.enableLegendClick) {
        this.handleLegendClick(params);
      } else {
        // Block ECharts default toggle when legendClick is disabled
        this.isInternalLegendChange = true;
        const chart = this.callbacks?.getChart();
        if (chart) {
          const action = params.selected[params.name] ? 'legendUnSelect' : 'legendSelect';
          chart.dispatchAction({ type: action, name: params.name });
        }
        this.isInternalLegendChange = false;
      }
    });
  }

  // ========================
  // LABEL CLICK (Category Selection)
  // ========================

  handleLabelClick(clickedCategory: string): void {
    if (!clickedCategory) return;

    if (this.data?.isMultiSelect) {
      const idx = this.selectedCategories.indexOf(clickedCategory);
      if (idx === -1) {
        this.selectedCategories = [...this.selectedCategories, clickedCategory];
      } else {
        this.selectedCategories = this.selectedCategories.filter(c => c !== clickedCategory);
      }
    } else {
      if (this.selectedCategories.length === 1 && this.selectedCategories[0] === clickedCategory) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedCategory];
      }
    }

    if (this.selectedCategories.length === 0) {
      this.resetCategoryHighlight();
      this.broadcastCategoryFilter([]);
    } else {
      this.highlightCategories(this.selectedCategories);
      this.broadcastCategoryFilter(this.selectedCategories);
    }
  }

  // ========================
  // Y-AXIS CLICK
  // ========================

  private handleYAxisClick(params: any): void {
    const chart = this.callbacks?.getChart();
    if (!chart) return;

    const axisIndex = params.yAxisIndex ?? 0;
    // ECharts may return formatted strings like "1,000" — strip commas before parsing
    const clickedValue = Number(String(params.value).replace(/,/g, ''));
    if (isNaN(clickedValue)) return;

    const allCategories = this.callbacks?.getCategories() || [];
    const option = chart.getOption() as any;
    const seriesList: any[] = option?.series || [];

    // Compute tick interval from the y-axis to determine match range
    const yAxisOpt = Array.isArray(option?.yAxis) ? option.yAxis[axisIndex] : option?.yAxis;
    const tickInterval = this.getYAxisTickInterval(yAxisOpt, seriesList);
    const halfInterval = tickInterval / 2;

    // Find categories whose series value falls within the clicked tick range
    const newMatches = new Set<string>();
    seriesList.forEach((s: any) => {
      if (!Array.isArray(s.data)) return;
      s.data.forEach((d: any, idx: number) => {
        const val = Number(typeof d === 'object' ? (d?.value ?? d) : d);
        if (!isNaN(val) && val >= clickedValue - halfInterval && val < clickedValue + halfInterval) {
          const category = (typeof d === 'object' ? d?.category : null) || allCategories[idx];
          if (category) newMatches.add(category);
        }
      });
    });

    const matched = Array.from(newMatches);

    if (this.data?.isMultiSelect) {
      // Multi-select: toggle matched categories into the existing selection
      const allAlreadySelected = matched.length > 0 && matched.every(c => this.selectedCategories.includes(c));
      if (allAlreadySelected) {
        // Deselect these categories
        this.selectedCategories = this.selectedCategories.filter(c => !matched.includes(c));
        this.selectedYAxisValues = this.selectedYAxisValues.filter(
          (v: { axisIndex: number; value: number }) => !(v.axisIndex === axisIndex && v.value === clickedValue),
        );
      } else if (matched.length > 0) {
        // Add new matches to selection
        const merged = [...this.selectedCategories];
        matched.forEach(c => { if (!merged.includes(c)) merged.push(c); });
        this.selectedCategories = merged;
        this.selectedYAxisValues = [...(this.selectedYAxisValues || []), { axisIndex, value: clickedValue }];
      }

      if (this.selectedCategories.length === 0) {
        this.selectedYAxisValue = null;
        this.selectedYAxisValues = [];
        this.resetCategoryHighlight();
        this.broadcastCategoryFilter([]);
      } else {
        this.highlightCategories(this.selectedCategories);
        this.broadcastCategoryFilter(this.selectedCategories);
      }
    } else {
      // Single-select: toggle on/off
      if (
        this.selectedYAxisValue &&
        this.selectedYAxisValue.axisIndex === axisIndex &&
        this.selectedYAxisValue.value === clickedValue
      ) {
        // Deselect
        this.selectedYAxisValue = null;
        this.selectedCategories = [];
        this.resetCategoryHighlight();
        this.broadcastCategoryFilter([]);
      } else if (matched.length > 0) {
        this.selectedYAxisValue = { axisIndex, value: clickedValue };
        this.selectedCategories = matched;
        this.highlightCategories(matched);
        this.broadcastCategoryFilter(matched);
      }
    }

    this.callbacks?.onReload(this.globalFilterService.getCurrentFilters());
  }

  private getYAxisTickInterval(yAxisOpt: any, seriesList: any[]): number {
    // Use explicit interval if set
    const explicit = Array.isArray(yAxisOpt?.interval) ? yAxisOpt.interval[0] : yAxisOpt?.interval;
    if (explicit && Number(explicit) > 0) return Number(explicit);

    // Derive from min/max/splitNumber
    const min = Number(Array.isArray(yAxisOpt?.min) ? yAxisOpt.min[0] : (yAxisOpt?.min ?? 0));
    const max = Number(Array.isArray(yAxisOpt?.max) ? yAxisOpt.max[0] : (yAxisOpt?.max ?? 0));
    if (max > min) {
      const splits = Number(yAxisOpt?.splitNumber ?? 5);
      return (max - min) / splits;
    }

    // Fallback: compute from actual data range
    let dataMin = Infinity;
    let dataMax = -Infinity;
    seriesList.forEach((s: any) => {
      if (!Array.isArray(s.data)) return;
      s.data.forEach((d: any) => {
        const val = Number(typeof d === 'object' ? (d?.value ?? d) : d);
        if (!isNaN(val) && isFinite(val)) {
          dataMin = Math.min(dataMin, val);
          dataMax = Math.max(dataMax, val);
        }
      });
    });

    if (isFinite(dataMin) && isFinite(dataMax) && dataMax > dataMin) {
      const splits = Number(yAxisOpt?.splitNumber ?? 5);
      const niceRange = (dataMax - Math.min(min || 0, dataMin)) * 1.2;
      return niceRange / splits;
    }

    return 50;
  }

  // ========================
  // LEGEND CLICK (Series Selection)
  // ========================

  private handleLegendClick(params: any): void {
    const clickedLegend = params.name;
    if (clickedLegend === 'Total') return;

    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const clickedIndex = seriesNames.indexOf(clickedLegend);
    if (clickedIndex === -1) return;

    this.isInternalLegendChange = true;

    if (this.data?.isMultiSelect) {
      const pos = this.currentlySelectedIndices.indexOf(clickedIndex);
      if (pos === -1) {
        this.currentlySelectedIndices = [...this.currentlySelectedIndices, clickedIndex];
      } else {
        this.currentlySelectedIndices = this.currentlySelectedIndices.filter(i => i !== clickedIndex);
      }
    } else {
      if (this.currentlySelectedIndices.length === 1 && this.currentlySelectedIndices[0] === clickedIndex) {
        this.currentlySelectedIndices = [];
      } else {
        this.currentlySelectedIndices = [clickedIndex];
      }
    }

    this.applyLegendSelectionAndBroadcast();
    this.isInternalLegendChange = false;
  }

  /** Handle series click as legend selection (for category charts with enableLegendClick) */
  private handleSeriesAsLegendClick(params: any): void {
    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const clickedIndex = seriesNames.indexOf(params.seriesName);
    if (clickedIndex === -1) return;

    if (this.data?.isMultiSelect) {
      const pos = this.currentlySelectedIndices.indexOf(clickedIndex);
      if (pos === -1) {
        this.currentlySelectedIndices = [...this.currentlySelectedIndices, clickedIndex];
      } else {
        this.currentlySelectedIndices = this.currentlySelectedIndices.filter(i => i !== clickedIndex);
      }
    } else {
      if (this.currentlySelectedIndices.length === 1 && this.currentlySelectedIndices[0] === clickedIndex) {
        this.currentlySelectedIndices = [];
      } else {
        this.currentlySelectedIndices = [clickedIndex];
      }
    }

    this.applyLegendSelectionAndBroadcast();
  }

  /** Handle slice click for pie/donut charts */
  private handleSliceClick(clickedName: string): void {
    if (!clickedName) return;

    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const clickedIndex = seriesNames.indexOf(clickedName);
    if (clickedIndex === -1) return;

    if (this.data?.isMultiSelect) {
      const pos = this.currentlySelectedIndices.indexOf(clickedIndex);
      if (pos === -1) {
        this.currentlySelectedIndices = [...this.currentlySelectedIndices, clickedIndex];
      } else {
        this.currentlySelectedIndices = this.currentlySelectedIndices.filter(i => i !== clickedIndex);
      }
    } else {
      if (this.currentlySelectedIndices.length === 1 && this.currentlySelectedIndices[0] === clickedIndex) {
        this.currentlySelectedIndices = [];
      } else {
        this.currentlySelectedIndices = [clickedIndex];
      }
    }

    this.applyLegendSelectionAndBroadcast();
  }

  /** Common logic after legend/slice selection changes */
  private applyLegendSelectionAndBroadcast(): void {
    const seriesNames = this.callbacks?.getSeriesNames() || [];

    if (this.currentlySelectedIndices.length === 0) {
      this.selectLegendsByIndices([]);
      this.broadcastLegendSync([]);
      this.updateFilterWithLegendKey([]);
    } else {
      this.selectLegendsByIndices(this.currentlySelectedIndices);
      this.broadcastLegendSync(this.currentlySelectedIndices);
      this.updateFilterWithLegendKey(
        this.currentlySelectedIndices.map(i => seriesNames[i]).filter(Boolean),
      );
    }
  }

  // ========================
  // HIGHLIGHT / VISUAL STATE
  // ========================

  highlightCategories(categories: string[]): void {
    // Use custom highlight if provided (e.g., color-based, custom opacity)
    if (this.callbacks?.onHighlightCategories) {
      this.callbacks.onHighlightCategories(categories);
      return;
    }

    const chart = this.callbacks?.getChart();
    if (!chart) return;

    const option = chart.getOption() as any;
    if (!option?.series) return;

    const allCategories = this.callbacks?.getCategories() || [];
    const categoryIndices = categories
      .map((cat: string) => allCategories.indexOf(cat))
      .filter((i: number) => i !== -1);

    const updatedSeries = applyOpacityToSeries(option.series, categoryIndices, 1, 0);
    chart.setOption({ series: updatedSeries });
  }

  resetCategoryHighlight(): void {
    this.selectedCategories = [];
    this.selectedYAxisValue = null;
    this.selectedYAxisValues = [];

    // Use custom reset if provided
    if (this.callbacks?.onResetCategoryHighlight) {
      this.callbacks.onResetCategoryHighlight();
      return;
    }

    const chart = this.callbacks?.getChart();
    if (!chart) return;

    const option = chart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = resetSeriesOpacity(option.series);
    chart.setOption({ series: updatedSeries });
  }

  highlightByGroup(groupValue: string): void {
    if (!groupValue) return;

    const allCategories = this.callbacks?.getCategories() || [];
    const regexStr = this.data?.chartConfig?.xAxis2GroupRegex;
    const matchingCategories = allCategories.filter(
      (cat) => extractGroupFromCategory(cat, regexStr) === groupValue,
    );
    if (matchingCategories.length === 0) return;

    // Toggle: if all matching are already selected, deselect them
    const allAlreadySelected = matchingCategories.every((c) =>
      this.selectedCategories.includes(c),
    );

    if (allAlreadySelected) {
      this.selectedCategories = this.selectedCategories.filter(
        (c) => !matchingCategories.includes(c),
      );
    } else {
      const newSelection = [...this.selectedCategories];
      matchingCategories.forEach((c) => {
        if (!newSelection.includes(c)) newSelection.push(c);
      });
      this.selectedCategories = newSelection;
    }

    if (this.selectedCategories.length === 0) {
      this.resetCategoryHighlight();
    } else {
      this.highlightCategories(this.selectedCategories);
    }
  }

  selectLegendsByIndices(selectedIndices: number[]): void {
    // Use custom legend selection if provided (e.g., donut dims non-selected slices)
    if (this.callbacks?.onSelectLegendsByIndices) {
      this.callbacks.onSelectLegendsByIndices(selectedIndices);
      return;
    }

    const chart = this.callbacks?.getChart();
    if (!chart) return;

    this.isInternalLegendChange = true;
    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const selected = buildLegendSelectedState(seriesNames, selectedIndices);
    chart.setOption({ legend: { selected } });
    this.isInternalLegendChange = false;
  }

  // ========================
  // BROADCASTING
  // ========================

  broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig = this.resolveListenerConfig(
      this.data?.labelListener,
      this.data?.labelListner,
      this.data?.legendListener,
      this.data?.filterListener,
      this.data?.filterListner,
    );

    // Silently update filter state so the API call carries the labelKey param
    // When labelKey is NOT set, filterKey takes over and extracts actual field values from raw data
    const labelKey = this.data?.labelKey;
    const filterKey = this.data?.filterKey;
    const primaryKey = labelKey || filterKey;
    if (primaryKey) {
      if (categories.length > 0) {
        let values: any[] = categories;
        // Only extract from sourceDataMap when labelKey is not explicitly set (filterKey is fallback)
        if (!labelKey && filterKey) {
          values = categories.map(cat => {
            const rawData = this.sourceDataMap.get(cat);
            if (rawData && typeof rawData === 'object' && filterKey in rawData) {
              return rawData[filterKey];
            }
            return cat;
          });
        }
        this.globalFilterService.updateFilterSelection(
          primaryKey, values, false, this.data.widgetId, false, listenerConfig,
        );
      } else {
        this.globalFilterService.deleteKey(primaryKey, false);
      }
    }

    // Compute per-listener extra params from filterKeyMap / listenerFilterKeys config.
    // Array format: ["key1","key2"] — extra keys get value from sourceDataMap or fallback to category name
    // Object format: { "filterKey": "dataField" } — maps filter key → data field name
    const filterKeyMapConfig = this.data?.filterKeyMap || this.data?.listenerFilterKeys;
    let listenerExtraParams: { [wId: string]: { [key: string]: any } } | undefined;
    if (filterKeyMapConfig && typeof filterKeyMapConfig === 'object' && categories.length > 0) {
      listenerExtraParams = {};
      for (const [targetWidgetId, config] of Object.entries(filterKeyMapConfig)) {
        const extraParams: { [key: string]: any } = {};

        if (Array.isArray(config)) {
          for (const extraKey of config as string[]) {
            const values: any[] = [];
            for (const cat of categories) {
              const rawData = this.sourceDataMap.get(cat);
              if (rawData && typeof rawData === 'object' && extraKey in rawData) {
                values.push(rawData[extraKey]);
              } else {
                values.push(cat);
              }
            }
            extraParams[extraKey] = values;
          }
        } else if (config && typeof config === 'object') {
          for (const [filterKey, dataField] of Object.entries(config)) {
            const values: any[] = [];
            for (const cat of categories) {
              const rawData = this.sourceDataMap.get(cat);
              if (rawData && typeof rawData === 'object' && (dataField as string) in rawData) {
                values.push(rawData[dataField as string]);
              } else {
                values.push(cat);
              }
            }
            extraParams[filterKey] = values;
          }
        }

        if (Object.keys(extraParams).length > 0) {
          listenerExtraParams[targetWidgetId] = extraParams;
        }
      }
      if (Object.keys(listenerExtraParams).length === 0) {
        listenerExtraParams = undefined;
      }
    }

    // Single emission: legendSync enriched with current filter state
    const filterData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(
        categories,
        this.data.widgetId,
        listenerConfig,
        this.data?.labelKey || this.data?.legendKey || this.data?.filterKey,
      ),
      ...(listenerExtraParams ? { listenerExtraParams } : {}),
    };
    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  broadcastLegendSync(selectedIndices: number[]): void {
    const listenerConfig = this.resolveListenerConfig(
      this.data?.legendListener,
      this.data?.legendListner,
      this.data?.filterListener,
      this.data?.filterListner,
    );

    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const selectedColors = this.getColorsForSelectedIndices(selectedIndices);
    const colorSyncEnabled = this.data?.chartConfig?.colorSyncEnabled === true;
    const legendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;

    const filterData = buildLegendSyncData(
      selectedIndices,
      seriesNames,
      this.data.widgetId,
      listenerConfig,
      { colorSyncEnabled, selectedColors, legendKey },
    );

    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  private updateFilterWithLegendKey(names: string[]): void {
    try {
      const legendListener = this.data?.legendListener || this.data?.legendListner;
      const legendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey;
      const filterKey = this.data?.filterKey;
      const legendFilterKey = legendKey || filterKey;
      const cloned = legendListener
        ? {
            ...this.data,
            filterListener: legendListener,
            ...(legendFilterKey ? { filterKey: legendFilterKey } : {}),
          }
        : this.data;
      // When legendKey is NOT set and filterKey is used, pass rawData for value extraction
      const rawData = (!legendKey && filterKey && names.length === 1)
        ? this.sourceDataMap.get(names[0])
        : (names.length === 1 ? this.sourceDataMap.get(names[0]) : undefined);
      this.chartFilterHandler.updateFilterSelection(
        cloned,
        names.length === 0 ? [] : names.length === 1 ? names[0] : names,
        rawData,
      );
    } catch (e) {}
  }

  // ========================
  // EXTERNAL LEGEND SYNC (receiving)
  // ========================

  private handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this.data?.widgetId) return;
    const chart = this.callbacks?.getChart();
    if (!chart) return;

    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const allCategories = this.callbacks?.getCategories() || [];

    // Reset if empty selection
    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      if (this.chartType === 'category') {
        // Only reset legend-related state; preserve category selection (e.g., Q1 2024 bar click)
        this.currentlySelectedIndices = [];
        this.selectLegendsByIndices([]);
      } else {
        // legend-only: check legendKey match
        const myLegendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;
        const sourceLegendKey = filters.legendKey;
        if (!sourceLegendKey || sourceLegendKey === myLegendKey) {
          this.currentlySelectedIndices = [];
          this.selectLegendsByIndices([]);
        }
      }
      return;
    }

    // Category filter (from x-axis clicks on other charts)
    if (filters.isCategoryFilter && filters.selectedLegends?.length > 0 && allCategories.length > 0) {
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingCategories = allCategories.filter((cat: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode)),
      );
      if (matchingCategories.length > 0) {
        this.selectedCategories = matchingCategories;
        this.highlightCategories(matchingCategories);
      }
      return;
    }

    if (!seriesNames.length) return;

    // Color-based sync
    const useColorSync = filters.colorSyncEnabled && filters.selectedColors?.length > 0;
    const isNameBasedFilter =
      !filters.isCategoryFilter &&
      filters.selectedLegends?.length > 0 &&
      (!filters.selectedIndices || filters.selectedIndices.length === 0);

    if (useColorSync) {
      this.syncByColor(filters.selectedColors, filters.sourceWidgetId);
    } else if (isNameBasedFilter) {
      this.syncByName(filters.selectedLegends, filters.sourceWidgetId);
    } else if (filters.selectedLegends?.length > 0) {
      this.syncByName(filters.selectedLegends, filters.sourceWidgetId);
    } else {
      this.syncByIndex(filters.selectedIndices || [], filters.sourceWidgetId);
    }

    // For category charts, reset category highlight only if no active category selection
    if (this.chartType === 'category' && this.selectedCategories.length === 0) {
      this.resetCategoryHighlight();
    }
  }

  private syncByColor(selectedColors: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data?.widgetId) return;
    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const config = this.data?.chartConfig || {};
    const colorConfig: ColorConfig = {
      colorMap: config.colorMap || {},
      topNColorsByStackPosition: config.topNColorsByStackPosition || [],
    };
    const matchingIndices = findIndicesByColors(selectedColors, seriesNames, colorConfig);
    if (matchingIndices.length === 0) return;
    this.currentlySelectedIndices = matchingIndices;
    this.selectLegendsByIndices(matchingIndices);
  }

  private syncByName(selectedLegends: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data?.widgetId) return;
    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const matchingIndices = findIndicesByNames(seriesNames, selectedLegends);
    if (matchingIndices.length === 0) return;
    this.currentlySelectedIndices = matchingIndices;
    this.selectLegendsByIndices(matchingIndices);
  }

  private syncByIndex(selectedIndices: number[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data?.widgetId) return;
    this.currentlySelectedIndices = selectedIndices;
    this.selectLegendsByIndices(selectedIndices);
  }

  // ========================
  // HELPERS
  // ========================

  private getColorsForSelectedIndices(indices: number[]): string[] {
    const seriesNames = this.callbacks?.getSeriesNames() || [];
    const config = this.data?.chartConfig || {};
    const colorConfig: ColorConfig = {
      colorMap: config.colorMap || {},
      topNColorsByStackPosition: config.topNColorsByStackPosition || [],
    };
    return getColorsForIndices(indices, seriesNames, colorConfig);
  }

  private resolveListenerConfig(...candidates: any[]): any {
    for (const c of candidates) {
      if (c && (!Array.isArray(c) || c.length > 0)) return c;
    }
    return [];
  }

  private handleNavigationAction(params: any): boolean {
    return executeNavigationFromConfig(this.router, this.data, params);
  }

  private buildNavigationContext(params: any): Record<string, any> {
    return {
      event: {
        name: params?.name,
        value: params?.value,
        seriesName: params?.seriesName,
        dataIndex: params?.dataIndex,
        componentType: params?.componentType,
        axisValue: params?.value,
      },
      data: this.data || {},
      context: {
        widgetId: this.data?.widgetId,
      },
      widgetId: this.data?.widgetId,
    };
  }

  private resolveState(action: any, context: Record<string, any>): Record<string, any> | undefined {
    const state = this.resolveMap(action.state, context);
    const payload = this.resolveMap(action.payload, context);
    if (action.stateKey) {
      return { ...(state || {}), [action.stateKey]: payload || {} };
    }
    if (state && payload) return { ...state, ...payload };
    return state || payload;
  }

  private resolveMap(source: any, context: Record<string, any>): Record<string, any> | undefined {
    if (!source || typeof source !== 'object') return undefined;
    const out: Record<string, any> = {};
    Object.keys(source).forEach((key) => {
      out[key] = this.resolveValue(source[key], context);
    });
    return out;
  }

  private resolveValue(value: any, context: Record<string, any>): any {
    if (typeof value === 'string') {
      if (value.startsWith('$')) return this.resolveToken(value, context);
      return this.replaceTokens(value, context);
    }
    if (Array.isArray(value)) return value.map((v) => this.resolveValue(v, context));
    if (value && typeof value === 'object') {
      const out: Record<string, any> = {};
      Object.keys(value).forEach((k) => (out[k] = this.resolveValue(value[k], context)));
      return out;
    }
    return value;
  }

  private replaceTokens(input: string, context: Record<string, any>): string {
    return String(input).replace(/\$[a-zA-Z0-9_.-]+/g, (token) => {
      const value = this.resolveToken(token, context);
      return value === undefined || value === null ? '' : String(value);
    });
  }

  private resolveToken(token: string, context: Record<string, any>): any {
    const path = token.replace(/^\$/, '');
    return path.split('.').reduce((acc: any, part: string) => (acc == null ? undefined : acc[part]), context);
  }
}

export function executeNavigationFromConfig(router: Router, data: any, params: any): boolean {
  const action = data?.navigateAction || (data?.navigateTo ? { route: data.navigateTo } : null);
  if (!action) return false;

  const rawPath = action.route || action.path || action.url || data?.navigateTo;
  if (!rawPath) return false;

  const context: Record<string, any> = {
    event: {
      name: params?.name,
      value: params?.value,
      seriesName: params?.seriesName,
      dataIndex: params?.dataIndex,
      componentType: params?.componentType,
      axisValue: params?.value,
    },
    data: data || {},
    context: {
      widgetId: data?.widgetId,
    },
    widgetId: data?.widgetId,
  };

  const replaceTokens = (template: string): string =>
    String(template || '').replace(/\$[a-zA-Z0-9_.]+/g, (token) => {
      const path = token.slice(1).split('.');
      let cur: any = context;
      for (const p of path) cur = cur?.[p];
      return cur == null ? '' : String(cur);
    });

  const resolveToken = (token: string): any => {
    const path = token.slice(1).split('.');
    let cur: any = context;
    for (const p of path) cur = cur?.[p];
    return cur;
  };

  const resolveValue = (value: any): any => {
    if (typeof value === 'string') {
      if (value.startsWith('$')) return resolveToken(value);
      return replaceTokens(value);
    }
    if (Array.isArray(value)) return value.map(resolveValue);
    if (value && typeof value === 'object') {
      const out: Record<string, any> = {};
      Object.keys(value).forEach((k) => (out[k] = resolveValue(value[k])));
      return out;
    }
    return value;
  };

  const resolveMap = (source: any): Record<string, any> | undefined => {
    if (!source || typeof source !== 'object') return undefined;
    const out: Record<string, any> = {};
    Object.keys(source).forEach((key) => (out[key] = resolveValue(source[key])));
    return out;
  };

  const normalizeListenerWidgets = (rawValue: any): string[] | undefined => {
    const resolvedValue = resolveValue(rawValue);
    const rawList = Array.isArray(resolvedValue)
      ? resolvedValue
      : typeof resolvedValue === 'string'
        ? resolvedValue.split(',')
        : [];

    const listeners = rawList
      .map((item: any) => String(item || '').trim())
      .filter((item: string) => !!item);

    return listeners.length ? listeners : undefined;
  };

  const resolveListenerWidgets = (): string[] | undefined =>
    normalizeListenerWidgets(
      action.listenerWidget ??
      action.listenerWidgets ??
      action.ListenerWidget ??
      action.ListenerWidgets ??
      action.listnerWidget ??
      action.listnerWidgets ??
      action.ListnerWidget ??
      action.ListnerWidgets ??
      action.Listnerwidget,
    );

  const resolveState = (): Record<string, any> | undefined => {
    const state = resolveMap(action.state);
    const payload = resolveMap(action.payload);
    const listenerWidgets = resolveListenerWidgets();
    const enrichedPayload = listenerWidgets
      ? {
          ...(payload || {}),
          filterListener: listenerWidgets,
          listenerWidget: listenerWidgets,
        }
      : payload;
    if (action.stateKey) {
      return { ...(state || {}), [action.stateKey]: enrichedPayload || {} };
    }
    if (state && enrichedPayload) return { ...state, ...enrichedPayload };
    return state || enrichedPayload;
  };

  const path = replaceTokens(String(rawPath));
  const queryParams = resolveMap(action.queryParams);
  const state = resolveState();
  const openInNewTab = action.openIn === 'new_tab' || action.openInNewTab === true;

  if (openInNewTab) {
    const url = router.serializeUrl(router.createUrlTree([path], { queryParams }));
    window.open(url, '_blank');
    return true;
  }

  router.navigate([path], { queryParams, state });
  return true;
}
